/**
 * Per-row CRUD for the Exception Detail Panel notes thread.
 *
 * Mirrors the backend ``/admin/exception-notes`` shape. Anchors to
 * the stable tuple identity ``(fp_code, exception_code, data_domain,
 * object_id)`` so a resolve+reopen cycle on the underlying tag never
 * orphans the thread.
 *
 * RBAC: callers MUST gate the UI surface with
 * ``canSeeExceptionNotes(user)``. The server is the source of
 * truth (``require_non_fp_user``); the UI check is convenience +
 * defense-in-depth. FP users short-circuit to no-ops here so a stale
 * URL or component leak can't issue requests.
 */
import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../services/api'
import { canSeeExceptionNotes } from '../services/auth'

export interface NoteAuthor {
  id: number
  name: string | null
  email: string | null
  role: string | null
}

export interface ExceptionNote {
  id: number
  body: string
  created_at: string
  updated_at: string | null
  author: NoteAuthor
  /** Who last edited the note - only set once a non-author Evo/PSC user
   * has edited it (null for unedited or author self-edits). */
  edited_by: NoteAuthor | null
  is_own: boolean
  can_edit: boolean
  can_delete: boolean
}

export interface NoteRowKey {
  fp_code: string
  exception_code: string
  data_domain: string
  object_id: string
}

export interface UseExceptionNotesResult {
  notes: ExceptionNote[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  create: (body: string) => Promise<ExceptionNote>
  update: (id: number, body: string) => Promise<ExceptionNote>
  remove: (id: number) => Promise<void>
}

/**
 * Hook returning the per-row note list plus CRUD callbacks.
 *
 * Pass ``key=null`` when no row is selected; the hook stays idle and
 * the consumer can flip ``key`` to start fetching.
 */
export function useExceptionNotes(
  key: NoteRowKey | null,
): UseExceptionNotesResult {
  const { user } = useAuth()
  const gated = canSeeExceptionNotes(user)
  const [notes, setNotes] = useState<ExceptionNote[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    if (!gated || !key) {
      setNotes([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await apiClient.get<ExceptionNote[]>(
        '/admin/exception-notes',
        { params: key },
      )
      setNotes(res.data)
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Request failed'
      setError(String(detail))
    } finally {
      setLoading(false)
    }
  }, [gated, key])

  // Re-fetch when the row key changes. Stringify so an object identity
  // change with identical contents (e.g. parent re-render) doesn't
  // refetch.
  const keyHash = key
    ? `${key.fp_code}|${key.exception_code}|${key.data_domain}|${key.object_id}`
    : null
  useEffect(() => {
    void refresh()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyHash])

  const create = useCallback(
    async (body: string): Promise<ExceptionNote> => {
      if (!gated || !key) {
        throw new Error('Notes are unavailable for this user / row.')
      }
      const res = await apiClient.post<ExceptionNote>(
        '/admin/exception-notes',
        { ...key, body },
      )
      setNotes((prev) => [...prev, res.data])
      return res.data
    },
    [gated, key],
  )

  const update = useCallback(
    async (id: number, body: string): Promise<ExceptionNote> => {
      if (!gated) {
        throw new Error('Notes are unavailable for this user / row.')
      }
      const res = await apiClient.patch<ExceptionNote>(
        `/admin/exception-notes/${id}`,
        { body },
      )
      setNotes((prev) =>
        prev.map((c) => (c.id === id ? res.data : c)),
      )
      return res.data
    },
    [gated],
  )

  const remove = useCallback(
    async (id: number): Promise<void> => {
      if (!gated) {
        throw new Error('Notes are unavailable for this user / row.')
      }
      await apiClient.delete(`/admin/exception-notes/${id}`)
      setNotes((prev) => prev.filter((c) => c.id !== id))
    },
    [gated],
  )

  return { notes, loading, error, refresh, create, update, remove }
}
